import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as HomeIcon } from 'assets/icons/home.svg';
import { ReactComponent as WorkIcon } from 'assets/icons/work.svg';
import useDashboardContext from 'contexts/dashboard.context';
import { LocationIcon, RmxEditIcon } from 'cokitchen-icons';
import DefaultAddressSetting from './default-address-settings';
import DeleteSavedAddress from './delete-saved-address';
import EditSavedAddressModal from './edit-saved-address-modal';
import { Bars } from 'react-loader-spinner';
import AddAddressModal from './add-address-modal';
import { Button, SideModal } from '@cokitchen/cokitchen-components';
import useAxios from 'hooks/useAxios';
import { BranchAddressType, QueryAllOrgBranchAddressRes } from 'models/branch-address.model';
import BranchAddressModal from '../branch-address-modal/branch-address-modal';
import DeleteBranchAddress from '../branch-address-modal/delete-branch-address-modal';
import { LocationObjectType } from 'models/location.model';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};
const SavedAddressesSettings = ({ isOpen, onClose }: Props) => {
  const {
    savedAddressList,
    addressListLoading,
    defaultAddress,
    defaultAddressLoading,
    activeCompany
  } = useDashboardContext();

  const {
    response,
    fetchData: fetchBranchAddresses,
    loading: isFetchingBranchAddress
  } = useAxios<QueryAllOrgBranchAddressRes>({
    url: `branches/${activeCompany?.id}`
  });
  const orgBranchAddress = response?.data?.data;

  const [openBranchAddress, setOpenBranchAddress] = useState(false);
  const [editBranchAddress, setEditBranchAddress] = useState(false);
  const [deleteBranchAddress, setDeleteBranchAddress] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeAddress, setActiveAddress] = useState<LocationObjectType | BranchAddressType>();
  const [homeAddress, setHomeAddress] = useState<LocationObjectType>();
  const [workAddress, setWorkAddress] = useState<LocationObjectType>();
  const [filteredAddress, setFilteredAddress] = useState<LocationObjectType[]>([]);
  const [name, setName] = useState('');

  const handleDelete = (address: LocationObjectType | BranchAddressType) => {
    setShowDeleteModal(true);
    setActiveAddress(address);
  };
  const handleEdit = (address: LocationObjectType | BranchAddressType) => {
    setActiveAddress(address);
    if (activeAddress) {
      setShowEditModal(true);
    }
  };
  const handleDeleteBranchAddress = (address: LocationObjectType | BranchAddressType) => {
    setDeleteBranchAddress(true);
    setActiveAddress(address);
  };
  const handleEditBranchAddress = (address?: LocationObjectType | BranchAddressType) => {
    setActiveAddress(address);
    setOpenBranchAddress(true);
    setEditBranchAddress(true);
  };

  useEffect(() => {
    fetchBranchAddresses();
  }, []);

  useMemo(() => {
    if (savedAddressList.length > 0) {
      savedAddressList.map((address) => {
        if (address.name === 'home') {
          setHomeAddress(address);
        }
        if (address.name === 'work') {
          setWorkAddress(address);
        }
      });
      // filter out home and work address
      const filtered = savedAddressList.filter(
        (address) =>
          address.name !== 'home' && address.name !== 'work' && address.id !== defaultAddress?.id
      );
      setFilteredAddress(filtered);
    }
  }, [savedAddressList, defaultAddress]);

  return (
    <SideModal
      closeBtnVariant='side'
      bodyClassName='w-[98vw] sm:w-[450px]'
      showTopNavbar
      isOpen={isOpen}
      onRequestClose={onClose}
      title={
        <div className='flex justify-between items-center mb-8'>
          <div>
            <h1 className='text-[20px] text-[#2E3031] font-medium'>Addresses</h1>
            <p className='text-[#6F7376] text-sm'>Manage Addresses</p>
          </div>
          <button
            className='text-[#E60A2B] font-medium mr-6 cursor-pointer'
            onClick={() => setShowAddModal(true)}>
            + Add
          </button>
        </div>
      }>
      <div className='w-full'>
        <div className='w-full mb-7'>
          <div className='w-full flex justify-between text-sm mb-3'>
            <p className='text-gray-10'>Default address</p>
            <p className='text-green-8 cursor-pointer' onClick={() => setShowAddressModal(true)}>
              Change
            </p>
          </div>
          <div
            className='w-full h-[51px] p-3 border-[0.8px] border-gray-13 text-sm text-[
#6F7376] rounded-sm'>
            {defaultAddressLoading ? (
              <p>Loading current address....</p>
            ) : (
              <div className='flex gap-3 items-center'>
                <LocationIcon className='w-4 h-4' />
                <div className='w-[90%]'>
                  <p className='line-clamp-1'>
                    {defaultAddress ? defaultAddress?.address : 'No default address'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Branch address section */}
        <div className='w-full mb-7'>
          <div className='w-full flex justify-between text-sm mb-3'>
            <p className='text-gray-10'>Branch addresses</p>
            <Button
              onClick={() => {
                setOpenBranchAddress(true);
                setEditBranchAddress(false);
              }}
              color='primary'
              variant='link'
              className='text-xs rounded-xl bg-red-1/80 px-3'>
              Add
            </Button>
          </div>
          {isFetchingBranchAddress ? (
            <div className='w-full h-[20vh] flex justify-center items-center'>
              <Bars color='#E60A2B' height='50' width='50' />
            </div>
          ) : orgBranchAddress && orgBranchAddress?.length > 0 ? (
            orgBranchAddress?.map((branchAddress, idx) => (
              <div
                key={branchAddress?.id}
                className='w-full h-[51px] rounded-sm border-[0.8px] border-gray-13 p-3 flex gap-1 justify-between items-center mb-3 cursor-pointer text-gray-9'>
                <div className='flex gap-4 items-center'>
                  <LocationIcon className='w-[14px] h-[14px]' />
                  <p className='text-gray-10 text-sm whitespace-nowrap'>Branch {idx + 1}</p>
                </div>
                <div>
                  <p className='text-gray-15 text-sm line-clamp-1 ml-1'>{branchAddress?.address}</p>
                </div>
                {
                  <div className='flex w-[20%] justify-end gap-3 items-center text-gray-9'>
                    <RmxEditIcon
                      className='cursor-pointer'
                      onClick={() => handleEditBranchAddress(branchAddress)}
                    />
                    <DeleteIcon
                      className='cursor-pointer'
                      onClick={() => handleDeleteBranchAddress(branchAddress)}
                    />
                  </div>
                }
              </div>
            ))
          ) : (
            <div>
              <p className='text-sm text-gray-12'>No saved address</p>
            </div>
          )}
        </div>
        {/* --------- */}
        <div className='w-full'>
          <p className='text-sm text-gray-10 mb-1'>Saved Addresses</p>
          <div className='w-full h-[51px] rounded-sm border-[0.8px] border-gray-13 p-3 flex gap-1 justify-between items-center mb-3 cursor-pointer text-gray-9'>
            <div className='flex gap-4 items-center'>
              <HomeIcon className='w-[14px] h-[14px]' />
              <p className='text-gray-10 text-sm'>Home</p>
            </div>
            <div
              className={`${homeAddress ? 'w-[70%]' : ''} cursor-pointer`}
              onClick={() => {
                if (!homeAddress) {
                  setName('Home');
                  setShowAddModal(true);
                }
              }}>
              <p className='text-gray-15 text-sm line-clamp-1'>
                {homeAddress ? `(${homeAddress.address})` : 'Add address'}
              </p>
            </div>
            {homeAddress && (
              <div className='flex w-[20%] justify-end gap-3 items-center text-gray-9'>
                <RmxEditIcon className='cursor-pointer' onClick={() => handleEdit(homeAddress)} />
                <DeleteIcon className='cursor-pointer' onClick={() => handleDelete(homeAddress)} />
              </div>
            )}
          </div>
          <div className='w-full h-[51px] rounded-sm border-[0.8px] border-gray-13 p-3 flex gap-4 justify-between items-center mb-3 cursor-pointer'>
            <div className='flex gap-4 items-center'>
              <WorkIcon className='w-[14px] h-[14px]' />
              <p className='text-gray-10 text-sm'>Work</p>
            </div>
            <div
              className={`${workAddress ? 'w-[70%]' : ''} cursor-pointer`}
              onClick={() => {
                if (!workAddress) {
                  setName('Work');
                  setShowAddModal(true);
                }
              }}>
              <p className='text-gray-15 text-sm line-clamp-1'>
                {workAddress ? workAddress.address : 'Add address'}
              </p>
            </div>
            {workAddress && (
              <>
                <RmxEditIcon className='cursor-pointer' onClick={() => handleEdit(workAddress)} />
                <DeleteIcon className='cursor-pointer' onClick={() => handleDelete(workAddress)} />
              </>
            )}
          </div>
          <div className='w-full'>
            {addressListLoading ? (
              <div className='w-full h-[20vh] flex justify-center items-center'>
                <Bars color='#E60A2B' height='50' width='50' />
              </div>
            ) : savedAddressList.length === 0 ? (
              <div>
                <p className='text-sm text-gray-10'>No saved address</p>
              </div>
            ) : (
              <div className='w-full h-[42vh] overflow-y-scroll no-scrollbar'>
                {filteredAddress.map((address, index) => (
                  <div
                    key={index}
                    className='flex justify-between w-full items-center h-[51px] border-[0.8px] border-gray-13 rounded-sm mb-3 p-3'>
                    <div className='flex w-[70%] gap-3 items-center'>
                      <LocationIcon className='w-4 h-4' />
                      <div className='w-[75%]'>
                        <p className='line-clamp-1 capitalize text-sm'>
                          {address.name === null ? 'Other' : address.name}({address.address})
                        </p>
                      </div>
                    </div>
                    <div className='flex w-[20%] justify-end gap-3 items-center text-gray-9'>
                      <RmxEditIcon className='cursor-pointer' onClick={() => handleEdit(address)} />
                      <DeleteIcon
                        className='cursor-pointer'
                        onClick={() => handleDelete(address)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {
        <DefaultAddressSetting
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
        />
      }
      <DeleteSavedAddress
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setActiveAddress(undefined);
        }}
        id={activeAddress?.id || ''}
      />
      <EditSavedAddressModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        activeAddress={activeAddress as LocationObjectType}
        setActiveAddress={setActiveAddress}
      />
      <AddAddressModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        name={name}
        setName={setName}
      />

      <DeleteBranchAddress
        isOpen={deleteBranchAddress}
        onClose={() => {
          setDeleteBranchAddress(false);
          setActiveAddress(undefined);
        }}
        refetchAllBranchesAddress={() => fetchBranchAddresses()}
        id={activeAddress?.id || ''}
      />
      <BranchAddressModal
        onClose={() => setOpenBranchAddress(false)}
        activeAddress={activeAddress}
        isOpen={openBranchAddress}
        isEdit={editBranchAddress}
        refetchAllBranchesAddress={() => fetchBranchAddresses()}
      />
    </SideModal>
  );
};

export default SavedAddressesSettings;
